import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Globe, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { specializationService } from '@/api/specialization.service';
import { Specialization, CreateSpecializationRequest, UpdateSpecializationRequest } from '@/api/types/specialization.types';
import { useLanguage } from '@/contexts/LanguageContext';

const getSpecializationSchema = (t: (key: string) => string) => z.object({
  name: z.object({
    en: z.string().min(1, t('specializations.englishNameRequired')),
    ar: z.string().min(1, t('specializations.arabicNameRequired')),
  }),
  description: z.object({
    en: z.string().optional(),
    ar: z.string().optional(),
  }).optional(),
  is_active: z.boolean().default(true),
});

type SpecializationFormData = z.infer<ReturnType<typeof getSpecializationSchema>>;

const AdminSpecializations = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const specializationSchema = getSpecializationSchema(t);
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSpecialization, setEditingSpecialization] = useState<Specialization | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<SpecializationFormData>({
    resolver: zodResolver(specializationSchema),
    defaultValues: {
      name: { en: '', ar: '' },
      description: { en: '', ar: '' },
      is_active: true,
    },
  });

  const fetchSpecializations = async () => {
    try {
      setLoading(true);
      const response = await specializationService.getSpecializations();
      setSpecializations(response.data);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('specializations.fetchError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecializations();
  }, []);

  const handleCreateSpecialization = async (data: SpecializationFormData) => {
    try {
      setSubmitting(true);
      await specializationService.createSpecialization(data as CreateSpecializationRequest);
      toast({
        title: t('specializations.createSuccess'),
        description: t('specializations.createSuccessDesc'),
      });
      setIsCreateDialogOpen(false);
      form.reset();
      fetchSpecializations();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('specializations.createError'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSpecialization = async (data: SpecializationFormData) => {
    if (!editingSpecialization) return;

    try {
      setSubmitting(true);
      await specializationService.updateSpecialization(
        editingSpecialization.id,
        data as UpdateSpecializationRequest
      );
      toast({
        title: t('specializations.updateSuccess'),
        description: t('specializations.updateSuccessDesc'),
      });
      setIsEditDialogOpen(false);
      setEditingSpecialization(null);
      form.reset();
      fetchSpecializations();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('specializations.updateError'),
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSpecialization = async (id: number) => {
    if (!confirm(t('specializations.confirmDelete'))) return;

    try {
      await specializationService.deleteSpecialization(id);
      toast({
        title: t('specializations.deleteSuccess'),
        description: t('specializations.deleteSuccessDesc'),
      });
      fetchSpecializations();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('specializations.deleteError'),
        variant: 'destructive',
      });
    }
  };

  const openEditDialog = (specialization: Specialization) => {
    setEditingSpecialization(specialization);
    form.reset({
      name: typeof specialization.name === 'string' 
        ? { en: specialization.name, ar: specialization.name }
        : specialization.name,
      description: typeof specialization.description === 'string'
        ? { en: specialization.description, ar: specialization.description }
        : specialization.description || { en: '', ar: '' },
      is_active: specialization.is_active,
    });
    setIsEditDialogOpen(true);
  };

  const filteredSpecializations = specializations.filter((spec) =>
    (typeof spec.name === 'string' ? spec.name : spec.name.en || spec.name.ar || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const getDisplayName = (name: string | { en: string; ar: string }) => {
    if (typeof name === 'string') return name;
    return name.en || name.ar || '';
  };

  const getDisplayDescription = (description?: string | { en: string; ar: string }) => {
    if (!description) return t('specializations.noDescription');
    if (typeof description === 'string') return description;
    return description.en || description.ar || t('specializations.noDescription');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t('specializations.title')}</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('specializations.subtitle')}
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              {t('specializations.addSpecialization')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">{t('specializations.createTitle')}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {t('specializations.createDesc')}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleCreateSpecialization)} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <FormField
                    control={form.control}
                    name="name.en"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs sm:text-sm">{t('specializations.englishName')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('specializations.englishNamePlaceholder')} {...field} className="text-xs sm:text-sm" />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name.ar"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs sm:text-sm">{t('specializations.arabicName')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('specializations.arabicNamePlaceholder')} {...field} className="text-xs sm:text-sm" />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <FormField
                    control={form.control}
                    name="description.en"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs sm:text-sm">{t('specializations.englishDescription')}</FormLabel>
                        <FormControl>
                          <Textarea placeholder={t('specializations.englishDescriptionPlaceholder')} {...field} className="text-xs sm:text-sm" />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description.ar"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs sm:text-sm">{t('specializations.arabicDescription')}</FormLabel>
                        <FormControl>
                          <Textarea placeholder={t('specializations.arabicDescriptionPlaceholder')} {...field} className="text-xs sm:text-sm" />
                        </FormControl>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 sm:p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-xs sm:text-sm">{t('specializations.active')}</FormLabel>
                        <FormDescription className="text-xs">
                          {t('specializations.activeDesc')}
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="w-full sm:w-auto">
                    {t('specializations.cancel')}
                  </Button>
                  <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                    {submitting ? t('specializations.creating') : t('specializations.createButton')}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl">{t('specializations.allSpecializations')}</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            {t('specializations.listDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <Input
              placeholder={t('specializations.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-xs sm:text-sm"
            />
          </div>
          
          {loading ? (
            <div className="text-center py-8 text-sm sm:text-base">{t('specializations.loading')}</div>
          ) : (
            <>
              {/* Mobile Cards View */}
              <div className="sm:hidden space-y-3">
                {filteredSpecializations.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    {t('specializations.noSpecializationsFound')}
                  </div>
                ) : (
                  filteredSpecializations.map((specialization) => (
                    <Card key={specialization.id} className="shadow-sm">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="font-medium text-sm truncate">{getDisplayName(specialization.name)}</span>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                              {getDisplayDescription(specialization.description)}
                            </p>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant={specialization.is_active ? 'default' : 'secondary'} className="text-xs">
                                {specialization.is_active ? (
                                  <div className="flex items-center gap-1">
                                    <Eye className="h-3 w-3" />
                                    <span>{t('specializations.active')}</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <EyeOff className="h-3 w-3" />
                                    <span>{t('specializations.inactive')}</span>
                                  </div>
                                )}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(specialization.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(specialization)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSpecialization(specialization.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">{t('specializations.name')}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('specializations.description')}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('specializations.status')}</TableHead>
                      <TableHead className="text-xs sm:text-sm">{t('specializations.created')}</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">{t('specializations.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSpecializations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-sm">
                          {t('specializations.noSpecializationsFound')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSpecializations.map((specialization) => (
                        <TableRow key={specialization.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">
                            <div className="flex items-center space-x-2">
                              <Globe className="h-4 w-4 text-muted-foreground" />
                              <span>{getDisplayName(specialization.name)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs truncate text-xs sm:text-sm">
                            {getDisplayDescription(specialization.description)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={specialization.is_active ? 'default' : 'secondary'} className="text-xs">
                              {specialization.is_active ? (
                                <div className="flex items-center space-x-1">
                                  <Eye className="h-3 w-3" />
                                  <span>{t('specializations.active')}</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-1">
                                  <EyeOff className="h-3 w-3" />
                                  <span>{t('specializations.inactive')}</span>
                                </div>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm">
                            {new Date(specialization.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(specialization)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteSpecialization(specialization.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">{t('specializations.editTitle')}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {t('specializations.editDesc')}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleUpdateSpecialization)} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="name.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('specializations.englishName')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('specializations.englishNamePlaceholder')} {...field} className="text-xs sm:text-sm" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('specializations.arabicName')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('specializations.arabicNamePlaceholder')} {...field} className="text-xs sm:text-sm" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <FormField
                  control={form.control}
                  name="description.en"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('specializations.englishDescription')}</FormLabel>
                      <FormControl>
                        <Textarea placeholder={t('specializations.englishDescriptionPlaceholder')} {...field} className="text-xs sm:text-sm" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description.ar"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs sm:text-sm">{t('specializations.arabicDescription')}</FormLabel>
                      <FormControl>
                        <Textarea placeholder={t('specializations.arabicDescriptionPlaceholder')} {...field} className="text-xs sm:text-sm" />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 sm:p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-xs sm:text-sm">{t('specializations.active')}</FormLabel>
                      <FormDescription className="text-xs">
                        {t('specializations.activeDesc')}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="w-full sm:w-auto">
                  {t('specializations.cancel')}
                </Button>
                <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? t('specializations.updating') : t('specializations.updateButton')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSpecializations;
